import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Gewerk, User, UserStatusFilter } from '@myapp/contract'
import { useAuthStore } from '../../features/auth'
import {
  useUsersInfiniteQuery,
  useArchiveUser,
  useRestoreUser,
  canManageUsers,
} from '../../entities/user'
import { useGewerkeInfiniteQuery } from '../../entities/gewerk'
import { Button } from '../../shared/ui/controls/Button/Button'
import { routes } from '../../shared/config/routes'
import PageLayout from '../../shared/ui/layouts/PageLayout/PageLayout'
import Loader from '../../shared/ui/feedback/Loader/Loader'
import { useDebounce } from '../../shared/hooks/useDebounce'
import { useInfiniteScrollSentinel } from '../../shared/hooks/useInfiniteScrollSentinel'
import { PAGE_LIMIT } from './model/constants'
import { StickyFilterBar } from '../../shared/ui/layouts/StickyFilterBar'
import { UserFilterBar } from './ui/UserFilterBar'
import { UserListItem } from './ui/UserListItem'

export function UserManagementPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const actorRoles = user?.roles ?? []
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<UserStatusFilter>('active')
  const [tradeId, setTradeId] = useState<number | null>(null)

  const debouncedSearch = useDebounce(search)

  // `page` is injected by useInfinitePaginated — passing it here would be dead weight.
  const filters = useMemo(
    () => ({
      status,
      limit: PAGE_LIMIT,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(tradeId !== null ? { tradeId } : {}),
    }),
    [status, debouncedSearch, tradeId]
  )

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useUsersInfiniteQuery(filters)

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: Boolean(hasNextPage),
    onLoadMore: handleLoadMore,
  })

  // Changing a filter restarts at page 1 — don't leave the user deep in a stale scroll position.
  useEffect(() => {
    document.getElementById('scrollableDiv')?.scrollTo({ top: 0 })
  }, [filters])

  const { data: gewerkeData } = useGewerkeInfiniteQuery({ limit: 500 })

  const archiveUser = useArchiveUser()
  const restoreUser = useRestoreUser()

  const gewerkLabels = useMemo(() => {
    const m = new Map<number, string>()
    const items: Gewerk[] = gewerkeData?.items ?? []
    items.forEach((g) => m.set(g.id, g.text))
    return m
  }, [gewerkeData])

  if (!canManageUsers(actorRoles)) {
    return <div className="p-6">Kein Zugriff</div>
  }

  const users: User[] = data?.items ?? []

  const handleArchive = async (id: string, name: string) => {
    if (!window.confirm(`Benutzer "${name}" wirklich archivieren?`)) return
    await archiveUser.mutateAsync(id)
  }

  const handleRestore = async (id: string, name: string) => {
    if (!window.confirm(`Benutzer "${name}" wiederherstellen?`)) return
    await restoreUser.mutateAsync(id)
  }

  const emptyMessage =
    status === 'archived'
      ? 'Keine archivierten Benutzer.'
      : 'Keine Benutzer für die aktuellen Filter.'

  return (
    <PageLayout
      pageTitle="User-Verwaltung"
      headerChildren={
        <Button
          variant="surface"
          className="w-[260px]"
          onClick={() => navigate(routes.users.create)}
        >
          Neuer Benutzer
        </Button>
      }
    >
      <div className="flex w-full flex-col gap-4">
        <StickyFilterBar>
          <UserFilterBar
            search={search}
            onSearchChange={setSearch}
            status={status}
            onStatusChange={setStatus}
            tradeId={tradeId}
            onTradeIdChange={setTradeId}
          />
        </StickyFilterBar>

        {isLoading && !users.length ? (
          <div className="my-10 flex w-full justify-center">
            <Loader />
          </div>
        ) : !users.length ? (
          <p className="my-10 w-full text-center font-['PT_Sans'] text-[20px] font-bold text-primary">
            {emptyMessage}
          </p>
        ) : (
          <div className="flex w-full flex-col gap-4">
            {users.map((u) => (
              <UserListItem
                key={u.id}
                item={u}
                actorRoles={actorRoles}
                actorId={user?.id}
                gewerkLabels={gewerkLabels}
                onArchive={handleArchive}
                onRestore={handleRestore}
              />
            ))}

            {hasNextPage && <div ref={sentinelRef} className="h-1" />}

            {isFetchingNextPage && (
              <div className="my-4 flex w-full justify-center">
                <Loader size="small" />
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
